<template>
  <svg
    ref="targetEl"
    :class="[classes.container, 'vs-icon']"
    data-testid="team-versus-icon"
    viewBox="0 0 35 35"
    xmlns="http://www.w3.org/2000/svg"
    height="100%"
    preserveAspectRatio="xMidYMid meet"
  >
    <g>
      <line
        v-if="horizontalLine"
        x1="-100"
        y1="50%"
        x2="0"
        y2="50%"
        stroke-width="1"
      />
      <circle cx="50%" cy="50%" r="48%" stroke-width="1"></circle>
      <line
        v-if="horizontalLine"
        x1="34"
        y1="50%"
        x2="100"
        y2="50%"
        stroke-width="1"
      />
      <svg viewBox="0 0 35 35">
        <text
          x="50%"
          :y="zerothPositionIsHomeTeam ? '53%' : '50%'"
          dominant-baseline="middle"
          text-anchor="middle"
        >
          {{ text }}
        </text>
      </svg>
    </g>
  </svg>
</template>
<script lang="ts">
import {
  composeEvent,
  composeEventCompetitors
} from "@fresh8/ad-data-composition/src/betData";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { awayFirstSports } from "@fresh8/ad-data-composition/src/lib/orderedCompetitorData";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { createUseStyles } from "@fresh8/vue-jss/src";

import { computed, defineComponent, PropType } from "vue";

const useStyles = createUseStyles({
  container: {
    position: "relative",
    minHeight: "24px",
    maxHeight: props => props.maxWidth,
    minWidth: "25px",
    maxWidth: props => props.maxHeight,
    height: "100%",
    zIndex: 1,

    "& circle": {
      height: "80%",
      stroke: props => props.borderColor,
      fill: props => props.backgroundColor || "#fff"
    },
    "& line": {
      height: "80%",
      stroke: props => props.borderColor
    },
    "& text": {
      fontSize: "14px",
      width: "10px",
      height: "10px",
      fontWeight: 600,
      fill: props => props.color,
      fontFamily: props => props.fontFamily,
      fontStyle: props => props.fontStyle,
      textTransform: props => props.textTransform,
      textDecoration: props => props.textDecoration
    }
  }
});

export default defineComponent({
  name: "TeamVersusIcon",
  props: {
    eventId: {
      type: String as PropType<string>,
      required: true
    },
    horizontalLine: {
      type: Boolean,
      required: false,
      default: false
    },
    maxWidth: {
      type: String,
      required: false,
      default: "30px"
    },
    maxHeight: {
      type: String,
      required: false,
      default: "30px"
    },
    forceText: {
      type: String,
      required: false,
      default: undefined
    }
  },
  setup(props) {
    const brandContext = injectBrand();

    const { targetEl } = composePopoverForm("Versus Icon", [
      {
        type: "color",
        name: "Background Color",
        get: () =>
          brandContext?.brand.value?.adTemplate?.team?.versusIcon
            ?.backgroundColor || "#fff",
        set: (value: string) => {
          if (brandContext?.brand.value) {
            brandContext.brand.value.adTemplate.team.versusIcon.backgroundColor = value;
          }
        }
      },
      {
        type: "color",
        name: "Border Color",
        get: () =>
          brandContext?.brand.value?.adTemplate?.team?.versusIcon
            ?.borderColor || "#fff",
        set: (value: string) => {
          if (brandContext?.brand.value) {
            brandContext.brand.value.adTemplate.team.versusIcon.borderColor = value;
          }
        }
      },
      {
        type: "font",
        get: () =>
          brandContext?.brand.value?.adTemplate?.team?.versusIcon?.font ||
          "#fff",
        set: (value: string) => {
          if (brandContext?.brand.value) {
            brandContext.brand.value.adTemplate.team.versusIcon.font = value;
          }
        }
      }
    ]);

    const zerothPositionIsHomeTeam = computed(() => {
      // tried using composeHomeAndAway options but that sometimes results in errors if options not available (eg promos)

      const event = composeEvent(props.eventId);
      const comp = composeEventCompetitors(props.eventId);
      const sport = event.value?.sport || "";

      return (
        comp.value?.[0].name.micro ===
          event.value?.competitors.home.name.micro && !awayFirstSports(sport)
      );
    });

    const text = computed(() => {
      if (props.forceText) return props.forceText;
      return zerothPositionIsHomeTeam.value ? "VS" : "@";
    });

    const reactiveStyles = computed(() => {
      if (brandContext?.adTemplate.value?.team) {
        return {
          horizontalLine: props.horizontalLine,
          maxHeight: props.maxHeight,
          maxWidth: props.maxWidth,
          backgroundColor:
            brandContext?.teamBranding.value.versusIcon.backgroundColor,
          borderColor: brandContext?.teamBranding.value.versusIcon.borderColor,
          ...(brandContext?.getFontStyle(
            brandContext?.teamBranding.value.versusIcon.font || ""
          ) || {})
        };
      } else {
        return {
          horizontalLine: props.horizontalLine,
          maxHeight: props.maxHeight,
          maxWidth: props.maxWidth,
          backgroundColor: brandContext?.getColorStyle(
            brandContext?.adTemplate.value?.backgroundColor || ""
          ),
          borderColor: brandContext?.getColorStyle(
            brandContext?.adTemplate.value?.marketBorderColor || ""
          ),
          ...(brandContext?.getFontStyle(
            brandContext?.adTemplate.value?.market.teamNameFont || ""
          ) || {})
        };
      }
    });

    const classes = useStyles(reactiveStyles);

    return {
      text,
      targetEl,
      classes,
      zerothPositionIsHomeTeam
    };
  }
});
</script>
